import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth, useMe } from '@/lib/auth'
import { useTRPC } from '@/lib/trpc'
import { Segmented } from '@/components/segmented'
import { ReportsSection } from '@/components/reports-section'

type Tab = 'account' | 'strategies' | 'reports'
/** the page's tabs, like Sonar's settings: the address bar remembers the open one */
const TABS: { value: Tab; label: string }[] = [
  { value: 'account', label: 'Account' },
  { value: 'strategies', label: 'Strategies' },
  { value: 'reports', label: 'Reports' },
]

export function SettingsPage() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { user } = useMe()
  const { signOut } = useAuth()
  const strategies = useQuery(trpc.strategy.list.queryOptions())
  const cancel = useMutation(
    trpc.strategy.cancel.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: trpc.strategy.list.queryKey() }),
    }),
  )
  const active = (strategies.data ?? []).filter((s) => ['active', 'scheduled', 'paused'].includes(s.status))
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  const tab: Tab = TABS.some((t) => t.value === requested) ? (requested as Tab) : 'account'
  const showTab = (next: Tab) => setParams(next === 'account' ? {} : { tab: next }, { replace: true })
  const strategiesCard = (
    <section className="card p-6">
      <h2 className="card-title">Strategies</h2>
      <p className="mt-1 text-sm text-muted">Stop every active strategy. Remaining tokens stay in your wallet.</p>
      <button
        type="button"
        className="btn btn-secondary mt-4 text-negative"
        disabled={active.length === 0 || cancel.isPending}
        onClick={() => active.forEach((s) => cancel.mutate({ id: s.id }))}
      >
        Stop all ({active.length})
      </button>
    </section>
  )
  return (
    <div className={`grid gap-6 *:min-w-0 ${tab === 'reports' ? 'max-w-4xl' : 'max-w-2xl'}`}>
      <Segmented value={tab} onChange={showTab} options={TABS} className="w-fit text-sm" />
      {tab === 'reports' ? <ReportsSection /> : tab === 'strategies' ? strategiesCard : (
        <>
      <section className="card p-6">
        <h2 className="card-title">Account</h2>
        <dl className="mt-3 grid gap-2 text-sm">
          <div className="flex justify-between"><dt className="text-muted">Email</dt><dd>{user?.email ?? '–'}</dd></div>
          <div className="flex justify-between"><dt className="text-muted">Account id</dt><dd className="font-mono text-xs">{user?.id}</dd></div>
        </dl>
        <button type="button" className="btn btn-secondary mt-5" onClick={signOut}>Sign out</button>
      </section>
        </>
      )}
    </div>
  )
}
